<template>
	<div class="training-calendar">
		<b-card no-body>
			<b-card-header class="d-flex justify-content-between align-items-center flex-wrap">
				<h4 class="mb-0">Training Sessions Calendar</h4>
				<div class="d-flex align-items-center">
					<b-form-datepicker
						v-model="startDate"
						size="sm"
						class="mr-1"
						:max="endDate || null"
						@input="reloadSessions"
						placeholder="Start Date"
					/>
					<b-form-datepicker
						v-if="isListView"
						v-model="endDate"
						size="sm"
						class="mr-1"
						:min="startDate || null"
						@input="reloadSessions"
						placeholder="End Date"
					/>
					<b-button size="sm" variant="outline-primary" @click="goToday">Today</b-button>
				</div>
			</b-card-header>
			<b-card-body>
				<FullCalendar ref="fc" :options="calendarOptions" />
			</b-card-body>
		</b-card>

		<b-modal id="session-details" :title="selectedEvent ? selectedEvent.title : ''" hide-footer v-if="selectedEvent">
			<div>
				<p><strong>Course:</strong> {{ selectedEvent && selectedEvent.extendedProps.event_title }}</p>
				<p><strong>Trainer:</strong> {{ selectedEvent && (selectedEvent.extendedProps.trainer || '—') }}</p>
				<p><strong>Venue:</strong> {{ selectedEvent && (selectedEvent.extendedProps.venue || '—') }}</p>
				<p><strong>Starts:</strong> {{ selectedEvent ? formatDate(selectedEvent.start) : '—' }}</p>
				<p><strong>Ends:</strong> {{ selectedEvent ? formatDate(selectedEvent.end) : '—' }}</p>
				<div v-if="selectedEvent && selectedEvent.extendedProps && selectedEvent.extendedProps.target_audience" class="mt-1">
					<p class="mb-25"><strong>Target Audience</strong></p>
					<p class="mb-25" v-if="selectedEvent.extendedProps.target_audience.companies && selectedEvent.extendedProps.target_audience.companies.length">
						<strong>Companies:</strong>
						<span class="ml-25">
							{{ formatCompanies(selectedEvent.extendedProps.target_audience.companies) }}
						</span>
					</p>
					<p class="mb-25" v-if="selectedEvent.extendedProps.target_audience.roles && selectedEvent.extendedProps.target_audience.roles.length">
						<strong>Roles:</strong>
						<span class="ml-25">
							{{ selectedEvent.extendedProps.target_audience.roles.join(', ') }}
						</span>
					</p>
					<p class="mb-25" v-if="selectedEvent.extendedProps.target_audience.users && selectedEvent.extendedProps.target_audience.users.length">
						<strong>Users:</strong>
						<span class="ml-25">
							{{ selectedEvent.extendedProps.target_audience.users.join(', ') }}
						</span>
					</p>
				</div>
				<div v-if="selectedEventDetails" class="mt-1">
					<p><strong>Status:</strong> {{ selectedEventDetails.status || '—' }}</p>
					<p><strong>Max Participants:</strong> {{ selectedEventDetails.max_participants || '—' }}</p>
					<p><strong>Accepted:</strong> {{ selectedEventDetails.accepted_count || 0 }}</p>
					<div v-if="userApplication">
						<b-badge :variant="getMyStatusVariant(selectedEventDetails)">
							{{ getMyStatusText(selectedEventDetails) }}
						</b-badge>
					</div>
				</div>
				<div class="mt-1 d-flex flex-wrap align-items-center">
					<b-button size="sm" variant="primary" class="mr-50 mb-50" @click="goViewEvent(selectedEventDetails)">View Details</b-button>
					<b-button v-if="showApplyButton" size="sm" variant="success" class="mr-50 mb-50" @click="openApplyModal" :disabled="canApplyInfo.loading">
						<span v-if="canApplyInfo.loading"><b-spinner small class="mr-25" />Checking...</span>
						<span v-else>Apply</span>
					</b-button>
				</div>
				<div v-if="!canApplyInfo.loading && selectedEventDetails && !canApplyInfo.can_apply" class="mt-50">
					<p class="text-muted small mb-25">Cannot apply because:</p>
					<ul class="small pl-1 mb-50">
						<li v-if="canApplyInfo.reasons && canApplyInfo.reasons.already_applied">Already applied.</li>
						<li v-if="canApplyInfo.reasons && canApplyInfo.reasons.status_not_open">Event not open.</li>
						<li v-if="canApplyInfo.reasons && canApplyInfo.reasons.deadline_passed">Application deadline passed.</li>
						<li v-if="canApplyInfo.reasons && canApplyInfo.reasons.sessions_ended">All sessions ended.</li>
						<li v-if="canApplyInfo.reasons && canApplyInfo.reasons.not_in_audience">Not in target audience.</li>
					</ul>
				</div>
				<b-button size="sm" variant="primary" @click="$bvModal.hide('session-details')">Close</b-button>
			</div>
		</b-modal>

		<b-modal id="apply-modal" title="Apply to Event" hide-footer v-model="showApplyModal">
			<div v-if="selectedEventDetails">
				<p class="small text-muted" v-if="isEventFull(selectedEventDetails)">Event is full. You may be waitlisted.</p>
				<b-form @submit.prevent="submitApplication">
					<b-form-group label="Reason *" label-for="apply-reason">
						<b-form-textarea id="apply-reason" v-model="applyForm.reason" rows="3" required></b-form-textarea>
					</b-form-group>
					<b-form-group label="Special Needs" label-for="apply-needs">
						<b-form-textarea id="apply-needs" v-model="applyForm.special_needs" rows="2"></b-form-textarea>
					</b-form-group>

					<div v-if="applySessions && applySessions.length && !(selectedEventDetails && selectedEventDetails.is_eft)" class="mb-1">
						<p class="mb-50"><strong>Session selection</strong></p>
						<div v-for="s in applySessions" :key="s.id" v-if="applySessionSelections[s.id]" class="border rounded p-75 mb-50">
							<b-form-checkbox
								v-model="applySessionSelections[s.id].is_attending"
								:disabled="applyLoading"
								@change="onApplySessionToggle(s)"
							>
								Attend session {{ s.sequence_no || s.sequence || '' }}
								<span class="text-muted" v-if="s.start_at && s.end_at">
									({{ formatSessionRange(s) }})
								</span>
							</b-form-checkbox>

							<div v-if="getApplySessionAddons(s).length" class="mt-50 ml-2">
								<p class="mb-25 text-muted small">Add-ons</p>
								<div v-for="a in getApplySessionAddons(s)" :key="a.id" class="mb-25">
									<b-form-checkbox
										:checked="getApplyAddonChecked(s.id, a)"
										:disabled="applyLoading || !applySessionSelections[s.id].is_attending || !!(a.is_required || a.required)"
										@change="(val) => setApplyAddonChecked(s.id, a, val)"
									>
										{{ a.label || a.code }}
										<b-badge v-if="a.is_required || a.required" variant="light-primary" class="ml-25">Required</b-badge>
									</b-form-checkbox>
								</div>
							</div>
						</div>
					</div>

					<div class="d-flex justify-content-end">
						<b-button size="sm" variant="outline-secondary" class="mr-50" @click="cancelApply">Cancel</b-button>
						<b-button size="sm" variant="primary" type="submit" :disabled="applyLoading">
							<b-spinner small v-if="applyLoading" class="mr-25" />
							Submit
						</b-button>
					</div>
				</b-form>
			</div>
			<div v-else class="text-muted">Loading...</div>
		</b-modal>
	</div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list'
import moment from 'moment'
import axiosIns from '@/libs/axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
	name: 'TrainingCalendar',
	components: { FullCalendar },
	data() {
		const today = moment().format('YYYY-MM-DD')
		return {
			// Date range filters
			startDate: today,
			endDate: null,
			// Prevent datesSet from overwriting the picker on initial render / goToday
			suppressNextDatesSetStartDate: true,
			currentViewType: 'dayGridMonth',
			lastLoadedViewStart: null,
			lastLoadedViewEnd: null,
			companyColorMap: {
				'North Dublin': '#007bff',
				'Dublin South': '#28a745',
				'North Leinster': '#17a2b8',
				'South Leinster': '#6610f2',
				'North Munster': '#fd7e14',
				'South Munster': '#6f42c1',
				'North Connacht & Ulster': '#dc3545',
				'South Connacht': '#20c997',
			},
			sessions: [],
			selectedEvent: null,
			selectedEventDetails: null,
			userApplications: [],
			canApplyInfo: { loading: false, can_apply: false, reasons: {}, meta: {} },
			showApplyModal: false,
			applyForm: { reason: '', special_needs: '' },
			applySessionSelections: {},
			applyLoading: false,
			calendarOptions: {
				plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
				initialView: 'dayGridMonth',
				headerToolbar: {
					left: 'prev,next today',
					center: 'title',
					right: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth'
				},
				events: [],
				eventClick: this.onEventClick,
				height: 'auto',
				nowIndicator: true,
				selectable: false,
				datesSet: this.onDatesSet,
				// eventContent will be set in created() to bind Vue methods
			}
		}
	},
	computed: {
		isListView() {
			return (this.currentViewType || '').indexOf('list') === 0 || (this.currentViewType || '').includes('list')
		},
		userApplication() {
			if (!this.selectedEventDetails) return null
			return this.getUserApplication(this.selectedEventDetails.id)
		}
		,
		showApplyButton() {
			// rely solely on backend decision plus presence of details
			return !!(this.selectedEventDetails && this.canApplyInfo.can_apply)
		},
		applySessions() {
			const s = this.getApplySessions()
			return (s || []).slice().sort((a, b) => (a.sequence_no || 0) - (b.sequence_no || 0))
		}
	},
	mounted() {
		this.reloadSessions()
		this.loadUserApplications()
	},
	created() {
		// Bind eventContent to allow rendering audience companies inside events
		this.calendarOptions.eventContent = (arg) => this.eventContent(arg)
	},
	watch: {
		showApplyModal(val) {
			if (val) {
				this.initApplySelections()
			}
		},
		selectedEventDetails() {
			if (this.showApplyModal) {
				this.initApplySelections()
			}
		}
	},
	methods: {
		reloadSessions() {
			this.fetchSessionsByRange()
		},
		fetchSessionsByRange() {
			// Backend keeps year segment but ignores it; use start/end dates
			const start = this.startDate
			const end = this.endDate
			// Smart defaults mirroring backend behavior
			let startDate = start
			let endDate = end
			if (startDate && !endDate) {
				endDate = moment(startDate).endOf('year').format('YYYY-MM-DD')
			} else if (!startDate && endDate) {
				startDate = moment(endDate).startOf('year').format('YYYY-MM-DD')
			} else if (!startDate && !endDate) {
				const now = moment()
				startDate = now.startOf('year').format('YYYY-MM-DD')
				endDate = now.endOf('year').format('YYYY-MM-DD')
			}
			const yearSegment = moment(startDate || endDate || undefined).isValid()
				? moment(startDate || endDate).year()
				: new Date().getFullYear()
			axiosIns.get(`event-sessions/year/${yearSegment}`, { params: { start_date: null, end_date: endDate } })
				.then(r => {
					this.sessions = r.data || []
					const events = this.mapSessionsToEvents(this.sessions)
					this.setCalendarEvents(events)
					this.lastLoadedViewStart = startDate
					this.lastLoadedViewEnd = endDate
				})
				.catch(err => {
					// eslint-disable-next-line no-console
					console.error('Failed to load sessions', err)
				})
		},
		eventContent(arg) {
			// Render title + company badges if available
			const title = arg.event.title || ''
			const ta = arg.event.extendedProps && arg.event.extendedProps.target_audience
				? arg.event.extendedProps.target_audience
				: null
			const companies = ta && Array.isArray(ta.companies) ? ta.companies : []
			let companiesHtml = ''
			if (companies.length) {
				if (this.isAllCompanies(companies)) {
					companiesHtml = `<span style=\"display:inline-block;padding:2px 6px;margin:2px;border-radius:10px;background:#343a40;color:#fff;font-size:10px;\">All companies</span>`
				} else {
					const badges = companies.map((c) => {
						const color = this.getCompanyColor(c)
						return `<span style=\"display:inline-block;padding:2px 6px;margin:2px;border-radius:10px;background:${color};color:#fff;font-size:10px;\">${c}</span>`
					}).join('')
					companiesHtml = badges
				}
			}
			const html = `<div><div>${title}</div>${companiesHtml ? `<div style=\"margin-top:2px;\">${companiesHtml}</div>` : ''}</div>`
			return { html }
		},
		getCompanyColor(title) {
			if (!title) return '#6c757d'
			const color = this.companyColorMap[title]
			return color || '#6c757d'
		},
		getAllCompaniesTitles() {
			return Object.keys(this.companyColorMap || {})
		},
		isAllCompanies(companies) {
			if (!Array.isArray(companies)) return false
			const all = this.getAllCompaniesTitles()
			if (!all.length) return false
			// All companies present (order-insensitive)
			return all.every(c => companies.includes(c))
		},
		formatCompanies(companies) {
			return this.isAllCompanies(companies) ? 'All companies' : (Array.isArray(companies) ? companies.join(', ') : '')
		},
		setCalendarEvents(events) {
			const ref = this.$refs.fc
			if (ref && typeof ref.getApi === 'function') {
				const api = ref.getApi()
				api.removeAllEvents()
				if (Array.isArray(events)) {
					events.forEach(ev => api.addEvent(ev))
				}
			} else {
				// Fallback in case ref isn't ready yet
				this.calendarOptions.events = events
			}
		},
		onDatesSet(arg) {
			this.currentViewType = (arg && arg.view && arg.view.type) ? arg.view.type : this.currentViewType
			const viewType = this.currentViewType || ''
			// Default Start Date based on active view interval
			if (arg && arg.start && !this.suppressNextDatesSetStartDate) {
				// Use FullCalendar provided interval start for accuracy
				this.startDate = moment(arg.start).format('YYYY-MM-DD')
			}
			this.suppressNextDatesSetStartDate = false
			// Avoid redundant reloads if interval hasn't changed
			const newStart = arg && arg.start ? moment(arg.start).format('YYYY-MM-DD') : this.startDate
			const newEnd = arg && arg.end ? moment(arg.end).format('YYYY-MM-DD') : this.endDate
			if (this.lastLoadedViewStart === newStart && this.lastLoadedViewEnd === newEnd) {
				return
			}
			// For list view, ensure an end date default exists
			if (this.isListView) {
				if (this.startDate && !this.endDate) {
					this.endDate = moment(this.startDate).endOf('year').format('YYYY-MM-DD')
				}
			} else {
				// For non-list views, we don't require endDate; clear to let backend smart-default
				this.endDate = null
			}
			// Reload sessions when the visible interval changes
			this.reloadSessions()
		},
		mapSessionsToEvents(sessions) {
			return sessions.map(s => {
				const startIso = s.start_at ? moment(s.start_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DDTHH:mm:ss') : null
				const endIso = s.end_at ? moment(s.end_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DDTHH:mm:ss') : startIso
				return {
					id: s.id,
					title: s.event_title || 'Session',
					start: startIso,
						end: endIso,
					extendedProps: {
						event_id: s.event_id,
						event_title: s.event_title,
						trainer: s.trainer,
						venue: s.venue,
						sequence_no: s.sequence_no,
						target_audience: s.target_audience || null
					}
				}
			})
		},
		onEventClick(info) {
			this.selectedEvent = info.event
			const eventId = info.event.extendedProps.event_id
			Promise.all([
				this.fetchEventDetails(eventId),
				this.fetchCanApply(eventId)
			]).finally(() => {
				this.$bvModal.show('session-details')
			})
		},
		goToday() {
			const ref = this.$refs.fc
			if (ref && typeof ref.getApi === 'function') {
				this.startDate = moment().format('YYYY-MM-DD')
				this.endDate = this.isListView ? moment(this.startDate).endOf('year').format('YYYY-MM-DD') : null
				this.suppressNextDatesSetStartDate = true
				// Trigger calendar navigation; onDatesSet will adjust startDate/endDate and reload
				ref.getApi().today()
			}
		},
		formatDate(dateObj) {
			return dateObj ? moment(dateObj).format('YYYY-MM-DD HH:mm') : '—'
		},
		fetchEventDetails(eventId) {
			if (!eventId) return Promise.resolve()
			return axiosIns.get(`getEventById/${eventId}`).then(r => {
				this.selectedEventDetails = r.data
			}).catch(() => { this.selectedEventDetails = null })
		},
		loadUserApplications() {
			axiosIns.get('dashboard/applications').then(r => {
				this.userApplications = r.data || []
			}).catch(() => { this.userApplications = [] })
		},
		getUserApplication(eventId) {
			return this.userApplications.find(a => a.event_id === eventId)
		},
		getMyStatusVariant(item) {
			const application = this.getUserApplication(item.id)
			if (!application) return 'dark'
			switch (application.status) {
				case 'accepted': return 'success'
				case 'rejected':
				case 'manager_rejected': return 'danger'
				case 'pending_manager_approval': return 'warning'
				case 'pending_admin_review': return 'info'
				case 'waitlisted': return 'secondary'
				case 'withdrawn':
				case 'cancelled':
				case 'draft': return 'dark'
				case 'submitted': return 'primary'
				default: return 'secondary'
			}
		},
		getMyStatusText(item) {
			const application = this.getUserApplication(item.id)
			if (!application) return 'No Application'
			switch (application.status) {
				case 'accepted': return 'Placed'
				case 'rejected':
				case 'manager_rejected': return 'Not Approved'
				case 'pending_manager_approval':
				case 'pending_admin_review':
				case 'submitted': return 'Pending Approval'
				case 'waitlisted': return 'Waitlisted'
				case 'withdrawn': return 'Withdrawn'
				case 'cancelled': return 'Cancelled'
				case 'draft': return 'Draft'
				default: return application.status
			}
		},
		isEventFull(item) {
			if (!item) return false
			const acceptedCount = item.accepted_count || 0
			const maxParticipants = item.max_participants || 0
			return acceptedCount >= maxParticipants && maxParticipants > 0
		},
		isManagerOrAdmin() {
			const userData = JSON.parse(localStorage.getItem('userData')) || {}
			const userRole = userData.role || ''
			return ['admin', 'supermanager', 'Administrator'].includes(userRole)
		},
		fetchCanApply(eventId) {
			if (!eventId) { this.canApplyInfo = { loading:false, can_apply:false, reasons:{}, meta:{} }; return }
			this.canApplyInfo.loading = true
			axiosIns.get(`events/${eventId}/can-apply`)
				.then(r => { this.canApplyInfo = { loading:false, ...r.data } })
				.catch(() => { this.canApplyInfo = { loading:false, can_apply:false, reasons:{ request_failed:true } } })
		},
		openApplyModal() {
			this.showApplyModal = true
		},
		cancelApply() {
			this.showApplyModal = false
			this.applyForm.reason = ''
			this.applyForm.special_needs = ''
			this.applySessionSelections = {}
		},
		getApplySessions() {
			const fromEvent = this.selectedEventDetails && Array.isArray(this.selectedEventDetails.sessions)
				? this.selectedEventDetails.sessions
				: null
			if (fromEvent && fromEvent.length) return fromEvent
			const eventId = this.selectedEventDetails && this.selectedEventDetails.id
			if (!eventId) return []
			return (this.sessions || []).filter(s => s.event_id === eventId)
		},
		initApplySelections() {
			if (this.selectedEventDetails && this.selectedEventDetails.is_eft) {
				this.applySessionSelections = {}
				return
			}
			const sessions = this.getApplySessions()
			const next = {}
			sessions.forEach(s => {
				const addons = this.getApplySessionAddons(s)
				const addonPrefs = {}
				addons.forEach(a => {
					addonPrefs[a.id] = !!(a.is_required || a.required)
				})
				next[s.id] = { is_attending: true, addon_preferences: addonPrefs }
			})
			this.applySessionSelections = next
		},
		getApplySessionAddons(session) {
			if (!session) return []
			const raw = session.addons || session.session_addons || session.add_ons || []
			const list = Array.isArray(raw) ? raw : []
			return list.slice().sort((a, b) => {
				const ao = (a && (a.sort_order ?? a.sort)) ?? 0
				const bo = (b && (b.sort_order ?? b.sort)) ?? 0
				if (ao !== bo) return ao - bo
				return String((a && (a.label || a.code)) || '').localeCompare(String((b && (b.label || b.code)) || ''))
			})
		},
		getApplyAddonChecked(sessionId, addon) {
			const s = this.applySessionSelections[sessionId]
			if (!s || !s.addon_preferences) return false
			return !!s.addon_preferences[addon.id]
		},
		setApplyAddonChecked(sessionId, addon, val) {
			const s = this.applySessionSelections[sessionId]
			if (!s) return
			if (addon && (addon.is_required || addon.required)) {
				this.$set(s.addon_preferences, addon.id, true)
				return
			}
			this.$set(s.addon_preferences, addon.id, !!val)
		},
		onApplySessionToggle(session) {
			const s = this.applySessionSelections[session.id]
			if (!s) return
			if (!s.is_attending) {
				const addons = this.getApplySessionAddons(session)
				addons.forEach(a => {
					this.$set(s.addon_preferences, a.id, false)
				})
			} else {
				const addons = this.getApplySessionAddons(session)
				addons.forEach(a => {
					if (a.is_required || a.required) this.$set(s.addon_preferences, a.id, true)
				})
			}
		},
		buildApplySessionSelectionsPayload() {
			if (this.selectedEventDetails && this.selectedEventDetails.is_eft) return null
			const sessions = this.getApplySessions()
			if (!sessions.length) return null
			return sessions.map(s => {
				const sel = this.applySessionSelections[s.id] || { is_attending: false, addon_preferences: {} }
				const addons = this.getApplySessionAddons(s)
				return {
					event_session_id: s.id,
					is_attending: !!sel.is_attending,
					addon_preferences: addons.map(a => ({
						event_session_addon_id: a.id,
						will_attend: !!(sel.addon_preferences && sel.addon_preferences[a.id])
					}))
				}
			})
		},
		validateApplySelections() {
			if (this.selectedEventDetails && this.selectedEventDetails.is_eft) return true
			const sessions = this.getApplySessions()
			if (!sessions.length) return true
			const anySelected = sessions.some(s => this.applySessionSelections[s.id] && this.applySessionSelections[s.id].is_attending)
			return anySelected
		},
		formatSessionRange(s) {
			if (!s || !s.start_at || !s.end_at) return ''
			const start = moment(s.start_at, 'YYYY-MM-DD HH:mm:ss')
			const end = moment(s.end_at, 'YYYY-MM-DD HH:mm:ss')
			if (!start.isValid() || !end.isValid()) return ''
			return `${start.format('YYYY-MM-DD HH:mm')} – ${end.format('HH:mm')}`
		},
		submitApplication() {
			if (!this.selectedEventDetails) return
			if (!this.applyForm.reason.trim()) return
			if (!this.validateApplySelections()) {
				this.$toast({
					component: ToastificationContent,
					props: {
						title: 'Select at least one session',
						text: 'Please select at least one session to attend before submitting.',
						icon: 'AlertTriangleIcon',
						variant: 'warning',
					},
				})
				return
			}
			const userData = JSON.parse(localStorage.getItem('userData')) || {}
			if (!userData.id) {
				this.$toast({
					component: ToastificationContent,
					props: {
						title: 'Not signed in',
						icon: 'AlertTriangleIcon',
						variant: 'danger',
					},
				})
				return
			}
			this.applyLoading = true
			const sessionSelections = this.buildApplySessionSelectionsPayload()
			const payload = {
				user_id: userData.id,
				status: 'submitted',
				apply_reason: this.applyForm.reason.trim(),
				special_needs: (this.applyForm.special_needs || '').trim() || null,
			}
			if (sessionSelections) payload.session_selections = sessionSelections
			axiosIns.post(`events/${this.selectedEventDetails.id}/applications`, payload).then(() => {
				this.cancelApply()
				this.loadUserApplications()
				this.fetchEventDetails(this.selectedEventDetails.id)
			}).finally(() => { this.applyLoading = false })
		},
		goViewEvent(ev) {
			if (!ev || !ev.id) return
			this.$bvModal.hide('session-details')
			this.$router.push({ name: 'dashboard-event-view', params: { id: ev.id }, query: { from: 'events' } })
		},
	}
}
</script>

<style scoped>
.training-calendar .fc { font-size: 0.85rem; }
.training-calendar .fc-event { cursor: pointer; }
</style>

